import { create } from 'zustand';
import type { UserSubscription } from '../types/subscription';
import { TIER_LIMITS } from '../constants/tiers';
import {
  getSubscription,
  startCheckout as startCheckoutService,
  getCustomerPortalUrl,
  onSubscriptionChange,
} from '../services/subscription';

interface SubscriptionState {
  subscription: UserSubscription | null;
  isLoading: boolean;
  error: string | null;

  setSubscription: (sub: UserSubscription) => void;
  loadSubscription: (userId: string) => Promise<void>;
  startCheckout: (userId: string, userEmail: string, tier: 'learner' | 'pro' | 'teams') => Promise<string>;
  openCustomerPortal: (userId: string) => Promise<string>;
  listenToChanges: (userId: string) => () => void;
  reset: () => void;
}

const DEFAULT_FREE: UserSubscription = {
  tier: 'free',
  status: 'active',
  lemonSqueezyCustomerId: null,
  lemonSqueezySubscriptionId: null,
  lemonSqueezyOrderId: null,
  currentPeriodStart: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  uploadsUsedThisPeriod: 0,
  uploadsIncluded: TIER_LIMITS.free.uploads.included,
  tutorQueriesUsedThisPeriod: 0,
  tutorQueriesIncluded: TIER_LIMITS.free.tutorQueries.included,
  meteredSubscriptionItemId: null,
  customerPortalUrl: null,
  updatePaymentMethodUrl: null,
  createdAt: new Date().toISOString(),
  upgradedAt: null,
  cancelledAt: null,
};

export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  subscription: null,
  isLoading: false,
  error: null,

  setSubscription: (sub) => set({ subscription: sub, isLoading: false, error: null }),

  loadSubscription: async (userId) => {
    set({ isLoading: true, error: null });
    try {
      const sub = await getSubscription(userId);
      set({ subscription: sub, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load subscription';
      set({ error: message, isLoading: false });
    }
  },

  startCheckout: async (userId, userEmail, tier) => {
    set({ isLoading: true, error: null });
    try {
      const { checkoutUrl } = await startCheckoutService({ userId, userEmail, tier });
      set({ isLoading: false });
      return checkoutUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Checkout failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  openCustomerPortal: async (userId) => {
    set({ isLoading: true, error: null });
    try {
      const { url } = await getCustomerPortalUrl(userId);
      set({ isLoading: false });
      return url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not open customer portal';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  listenToChanges: (userId) => {
    return onSubscriptionChange(userId, (sub) => {
      set({ subscription: sub, isLoading: false, error: null });
    });
  },

  reset: () => set({ subscription: { ...DEFAULT_FREE }, isLoading: false, error: null }),
}));
